import {
  parseDelimitedRows,
  type ParsedActivityRow
} from "./catalogModel";
import type { ModuleBuilderDraft, InstrumentDraft, ReadinessItem } from "./types";

/**
 * Parse the canonical tab-separated activities text into rows.
 * Hands the WHOLE text to a quote-aware CSV/TSV parser so newlines inside
 * quoted cells (multi-line descriptions copied from Excel/Sheets) stay
 * with their cell instead of breaking the row.
 */
export function parseActivities(instrument: InstrumentDraft): ParsedActivityRow[] {
  const text = instrument.activitiesPasteText;
  if (!text) return [];
  return parseDelimitedRows(text, "\t").map((cells) => ({
    title: (cells[0] ?? "").trim(),
    description: (cells[1] ?? "").trim(),
    category: (cells[2] ?? "").trim()
  }));
}

/**
 * Serialize parsed rows to canonical tab-separated text. Preserves trailing
 * empty rows so the grid can keep newly-added blank rows visible for editing.
 */
export function serializeActivities(rows: ParsedActivityRow[]): string {
  return rows
    .map((row) => [row.title, row.description, row.category].map(escapeTsv).join("\t"))
    .join("\n");
}

/** Serialize rows using an arbitrary single-character separator (for the Paste view). */
export function serializeActivitiesWithSep(
  rows: ParsedActivityRow[],
  sep: "\t" | "," | "|"
): string {
  return rows
    .map((row) =>
      [row.title, row.description, row.category]
        .map((value) => escapeForSep(value, sep))
        .join(sep)
    )
    .join("\n");
}

function escapeForSep(value: string, sep: "\t" | "," | "|"): string {
  const text = value ?? "";
  if (text.includes(sep) || text.includes("\n") || text.includes('"')) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function escapeTsv(value: string): string {
  const text = value ?? "";
  if (text.includes("\t") || text.includes("\n") || text.includes('"')) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

export function activitiesToTypeListText(rows: ParsedActivityRow[]): string {
  return rows
    .filter((row) => row.title.trim() || row.description.trim() || row.category.trim())
    .map((row) => {
      if (row.description && row.category) {
        return `${row.title} | ${row.description} | ${row.category}`;
      }
      if (row.description) {
        return `${row.title} | ${row.description}`;
      }
      if (row.category) {
        return `${row.title} · ${row.category}`;
      }
      return row.title;
    })
    .join("\n");
}

export function typeListToActivities(text: string): ParsedActivityRow[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const pipeParts = line.split("|").map((value) => value.trim());
      if (pipeParts.length >= 2) {
        return {
          title: pipeParts[0] ?? "",
          description: pipeParts[1] ?? "",
          category: pipeParts[2] ?? ""
        };
      }
      const dotParts = line.split("·").map((value) => value.trim());
      return {
        title: dotParts[0] ?? line,
        description: "",
        category: dotParts[1] ?? ""
      };
    })
    .filter((row) => row.title);
}

export function duplicateTitlesIn(rows: ParsedActivityRow[]): string[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const key = row.title.trim().toLowerCase();
    if (!key) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const dups: string[] = [];
  counts.forEach((count, name) => {
    if (count > 1) dups.push(name);
  });
  return dups;
}

export function computeReadiness(
  draft: ModuleBuilderDraft,
  parsedByInstrumentId: Record<string, ParsedActivityRow[]>,
  sourceDetailStatus?: {
    /** Sids whose detail has actually arrived in instrumentDetailById. */
    loaded: ReadonlySet<string>;
    /** Sids whose fetch has failed and won't auto-retry. */
    failed: ReadonlySet<string>;
  },
  catalog?: {
    /** True once the catalog request has succeeded and flatByQuestionId is
     *  populated. While false, catalog-id questions can't be resolved and
     *  buildCsv would emit IDs-as-titles with no options. */
    loaded: boolean;
    /** Non-null when the catalog fetch has errored. Surfaced as a Fix line
     *  rather than a Loading line. */
    error: string | null;
  }
): { items: ReadinessItem[]; blocking: number; warnings: number } {
  const items: ReadinessItem[] = [];
  let blocking = 0;
  let warnings = 0;

  if (!draft.targetNetworkId) {
    blocking++;
    items.push({
      status: "bad",
      tag: "Required",
      text: "Pick a target network (top of page)."
    });
    return { items, blocking, warnings };
  }

  if (!draft.moduleTitle.trim()) {
    warnings++;
    items.push({
      status: "warn",
      tag: "Note",
      text: "Module title is empty — recommended for sharing."
    });
  } else {
    items.push({ status: "ok", tag: "OK", text: `Module: ${draft.moduleTitle.trim()}` });
  }

  if (!draft.instruments.length) {
    blocking++;
    items.push({ status: "bad", tag: "Required", text: "Add at least one instrument." });
    return { items, blocking, warnings };
  }

  // Within-module duplicate checks (cross-instrument) — still valuable since
  // two instruments in the same draft with the same title is almost always a
  // bug (and would collapse on import).
  const allInstrumentTitlesLower: string[] = [];
  const allAssessmentTitlesLower: string[] = [];
  const allActivityTitlesLower: string[] = [];

  for (const instr of draft.instruments) {
    const title = instr.title.trim();
    if (!title) {
      blocking++;
      items.push({
        status: "bad",
        tag: "Required",
        text: `Instrument ${draft.instruments.indexOf(instr) + 1} needs a title.`
      });
    } else {
      allInstrumentTitlesLower.push(title.toLowerCase());
      if (instr.sourceInstrumentId) {
        items.push({
          status: "ok",
          tag: "Reuse",
          text: `Reusing existing instrument "${title}".`
        });
      }
    }

    const asmtTitle = instr.assessmentSettings.title.trim();
    if (!asmtTitle) {
      blocking++;
      items.push({
        status: "bad",
        tag: "Required",
        text: `${title || "Instrument"} needs an assessment title (otherwise import would create one assessment per activity).`
      });
    } else {
      allAssessmentTitlesLower.push(asmtTitle.toLowerCase());
      if (instr.sourceAssessmentId) {
        items.push({
          status: "ok",
          tag: "Reuse",
          text: `Reusing existing assessment "${asmtTitle}" under ${title || "instrument"}.`
        });
      }
    }

    const rows = parsedByInstrumentId[instr.id] ?? [];
    if (!rows.length) {
      blocking++;
      items.push({
        status: "bad",
        tag: "Required",
        text: `${title || "Instrument"} needs at least one activity.`
      });
    } else {
      items.push({
        status: "ok",
        tag: "OK",
        text: `${title || "Instrument"}: ${rows.length} activit${rows.length === 1 ? "y" : "ies"}`
      });
    }

    const dups = duplicateTitlesIn(rows);
    if (dups.length) {
      blocking++;
      items.push({
        status: "bad",
        tag: "Fix",
        text: `Duplicate activity name(s) in ${title || "instrument"}: ${dups.join(", ")}`
      });
    }
    for (const r of rows) {
      const lower = r.title.trim().toLowerCase();
      if (lower) allActivityTitlesLower.push(lower);
    }

    const qids = draft.instrumentQuestionIdsById[instr.id] ?? [];
    if (instr.sourceInstrumentId) {
      // Sourced instruments inherit their questions from the source row;
      // they're loaded into instrumentDetailById and rendered read-only.
      // No question selection is required (or even possible) in the draft —
      // but the export path emits the inherited questions straight from the
      // loaded detail, so we must block export until that detail has loaded.
      //
      // Gate on POSITIVE presence in `loaded`, not absence from a loading
      // set: between the render where sourceInstrumentId is set and the
      // commit where the fetch effect fires, a "loading" set would still be
      // empty for this sid. Treating "not loaded and not failed" as loading
      // collapses that pre-effect tick into the loading state and keeps
      // Export gated.
      const sid = instr.sourceInstrumentId;
      if (sourceDetailStatus?.failed.has(sid)) {
        blocking++;
        items.push({
          status: "bad",
          tag: "Fix",
          text: `Couldn't load inherited questions for ${title || "instrument"}. Re-edit the title to retry.`
        });
      } else if (!sourceDetailStatus?.loaded.has(sid)) {
        blocking++;
        items.push({
          status: "bad",
          tag: "Loading",
          text: `Loading inherited questions for ${title || "instrument"}…`
        });
      } else {
        items.push({
          status: "ok",
          tag: "Reuse",
          text: `${title || "Instrument"}: questions inherited from existing instrument.`
        });
      }
    } else if (!qids.length) {
      blocking++;
      items.push({
        status: "bad",
        tag: "Required",
        text: `${title || "Instrument"} needs at least one question.`
      });
    } else {
      // Catalog questions (non-custom) need the catalog loaded so buildCsv
      // can resolve them to titles, prompts, and answer options. Without
      // this gate a saved draft can export with the question IDs as titles
      // and no answer options. Skip the check for instruments that only
      // contain custom questions — those are fully self-describing in the
      // draft's overridesById entries.
      const hasCatalogQuestion = qids.some((id) => !id.startsWith("custom-"));
      if (hasCatalogQuestion && catalog && !catalog.loaded) {
        blocking++;
        if (catalog.error) {
          items.push({
            status: "bad",
            tag: "Fix",
            text: `Couldn't load the question catalog (${title || "instrument"}). Refresh to retry.`
          });
        } else {
          items.push({
            status: "bad",
            tag: "Loading",
            text: `Loading the question catalog for ${title || "instrument"}…`
          });
        }
      } else {
        items.push({
          status: "ok",
          tag: "OK",
          text: `${title || "Instrument"}: ${qids.length} question${qids.length === 1 ? "" : "s"}`
        });
      }
    }

    const overrides = draft.instrumentQuestionOverridesById[instr.id] ?? {};
    const customs = qids.filter((id) => id.startsWith("custom-"));
    if (customs.length) {
      const incomplete = customs.filter((id) => {
        const ov = overrides[id];
        return !ov || !ov.title?.trim() || !ov.prompt?.trim();
      });
      if (incomplete.length) {
        blocking++;
        items.push({
          status: "bad",
          tag: "Required",
          text: `${incomplete.length} custom question(s) need both a title and a prompt.`
        });
      }
    }

    if (instr.assessmentSettings.requireSelfAssessment) {
      const missingPrompt = qids.filter((id) => {
        if (id.startsWith("custom-")) {
          return !overrides[id]?.selfAssessmentPrompt?.trim();
        }
        return overrides[id]?.selfAssessmentPrompt === ""; // explicitly cleared
      });
      if (missingPrompt.length) {
        warnings++;
        items.push({
          status: "warn",
          tag: "Note",
          text: `Self-assessment is on, but ${missingPrompt.length} question(s) have an empty self-assessment prompt.`
        });
      }
    }
  }

  // Cross-instrument: instrument titles
  const dupInstr = findDuplicates(allInstrumentTitlesLower);
  if (dupInstr.length > 0) {
    blocking++;
    items.push({
      status: "bad",
      tag: "Fix",
      text: `Duplicate instrument title(s) within this module: ${dupInstr.join(", ")}`
    });
  }

  // Cross-instrument: assessment titles. NOT a blocker — assessments dedup on
  // (title, description, instrumentId, sponsorId), so same title under
  // different instruments yields different DB rows. Surface as a warning so
  // the user can spot accidental collisions without gating export.
  const dupAsmt = findDuplicates(allAssessmentTitlesLower);
  if (dupAsmt.length > 0) {
    warnings++;
    items.push({
      status: "warn",
      tag: "Note",
      text: `Same assessment title used in multiple instruments (will create separate DB rows): ${dupAsmt.join(", ")}`
    });
  }

  // Cross-instrument: activity titles. Same reasoning — activities dedup on
  // (title, description, assessmentId, sponsorId). Within-instrument
  // duplicates are still a blocker (handled per-instrument above).
  const dupActivity = findDuplicates(allActivityTitlesLower);
  if (dupActivity.length > 0) {
    warnings++;
    items.push({
      status: "warn",
      tag: "Note",
      text: `Same activity title used in multiple instruments (will create separate DB rows): ${dupActivity.join(", ")}`
    });
  }

  return { items, blocking, warnings };
}

function findDuplicates(values: string[]): string[] {
  const seen = new Set<string>();
  const dups = new Set<string>();
  for (const v of values) {
    if (seen.has(v)) dups.add(v);
    seen.add(v);
  }
  return Array.from(dups);
}
